import {
  action,
  atom,
  onConnect,
  reaction,
  withAssign,
} from '@reatom/framework'

import { Array, AsyncStateN, Iterable, pipe, Result } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { fetchPairRequestAction, pairRegistry } from '@root/entities/pair'
import { PairAddress } from '@root/entities/pair/model'
import { Trade } from '@root/entities/trade'
import { trackingWalletAlertEvent } from '@root/features/realtime/reatom-websocket-main'
import { environmentAtom } from '@root/shared'
import { reatomDataTable } from '@root/shared/ui-kit'

import { TrackedWalletSwapAlert } from '../model'
import { createWalletAlertsTableColumns } from '../ui/wallet-alerts-table/wallet-alerts-table-columns'

import { walletTrackingAddedEvent } from './reatom-add-wallet-tracker'
import { walletTrackingDeletedEvent } from './reatom-delete-wallet-tracking'
import { walletTrackingUpdatedEvent } from './reatom-edit-tracking-wallet-form'
import { fetchWalletTrackerAlertsRequestAction } from './wallet-tracker-requests'

import type { Order } from '@blade/std-lib'
import type { AtomReturn } from '@reatom/framework'
import type { Network } from '@root/entities/network/model'
import type { PairModel } from '@root/entities/pair'
import type { TableData } from '@root/shared/ui-kit'

const MAX_ITEMS = 100
/**
 * Whether the alerts panel is actually on screen.
 *
 * This used to be read off whether the table atom had subscribers, which is
 * the same thing only while nothing else holds it. The list is now kept
 * loaded for the life of the app so that opening the panel shows what is
 * already there instead of a spinner — and that made the old test true
 * forever, so every trade went into a table nobody could see and the badge
 * beside it never moved. The panel says so itself now.
 */
export const alertsPanelShownAtom = atom(false, 'alertsPanelShownAtom')

const takeMaxItems = Array.take(MAX_ITEMS)

export type AlertsTableColumnId =
  | 'timestamp'
  | 'currency'
  | 'price'
  | 'type'
  | 'amount-usd'
  | 'amount-native'
  | 'mcap'
  | 'total-base'
  | 'total-usd'
  | 'u-pnl-usd'
  | 'maker'
  | 'action'

const reatomTrackingWalletAlertsTable = () => {
  const pausedAtom = atom(false, 'pausedAtom')

  const paramsAtom = atom(
    (ctx) => ({ network: ctx.spy(selectedNetworkAtom) }),
    'paramsAtom',
  )
  const tableModel = reatomDataTable<
    TrackedWalletSwapAlert.Alert,
    AlertsTableColumnId,
    AtomReturn<typeof paramsAtom>
  >({
    getId: (it) => it.id,
    columns: createWalletAlertsTableColumns(),
    paramsAtom,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'wallet-alerts-table',
    loadItems: async (ctx, { params }) => {
      const result = await fetchWalletTrackerAlertsRequestAction(ctx, {
        network: params.network,
      })
      /* The rows that came without a pair are deliberately left without one.
         Resolving them here froze the tab: calling the resolver once per row
         inside this load put up to fifty reatom actions in one transaction,
         and the main thread stopped for **24 seconds** on staging
         2026-10-03 — measured as a 30-second hole in the network timeline
         between `get-alerts` returning and the sixteen `pair-info` requests
         that came out the other side of it.
         A row draws fine without a pair; only its buy button is missing
         until the next load. Doing this properly means resolving the
         distinct addresses outside the load, which is still to build. */
      return Result.map(
        result,
        (it): TableData<TrackedWalletSwapAlert.Alert> => ({
          data: it.data,
          hasNext: false,
        }),
      )
    },
  })

  const pausedReaction = reaction((ctx) => {
    const paused = ctx.spy(pausedAtom)

    if (paused) {
      return
    }
    flushBufferAction(ctx)
  }, 'pausedReaction')

  /** What was held while the panel was shut goes in the moment it opens. */
  const shownReaction = reaction((ctx) => {
    if (!ctx.spy(alertsPanelShownAtom) || ctx.get(pausedAtom)) {
      return
    }
    flushBufferAction(ctx)
  }, 'shownReaction')

  const handleConnectedAction = action((ctx) => {
    const paused = ctx.get(pausedAtom)
    if (paused) {
      return
    }
    flushBufferAction(ctx)
  }, 'handleConnectedAction')

  /**
   * Hangs a pair on the rows that arrived without one.
   *
   * Asked for once per address however many trades of it come in, and
   * forgotten again when the answer lands so that a pair which appears later
   * can be asked for again.
   */
  const pending = new Set<string>()
  const attachPairAction = action(
    (ctx, pairAddress: PairAddress, pair: PairModel) => {
      const withPair = (rows: ReadonlyArray<TrackedWalletSwapAlert.Alert>) =>
        rows.map((row) =>
          row.pair === undefined && row.pairAddress === pairAddress
            ? { ...row, pair, quoteCurrency: pair.quote }
            : row,
        )

      const buffer = ctx.get(unseenAlertsBufferAtom)
      if (buffer.length > 0) {
        unseenAlertsBufferAtom(ctx, withPair(buffer))
      }

      const state = ctx.get(tableModel)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }
      tableModel.setState(
        ctx,
        AsyncStateN.success({
          ...state.value,
          data: withPair(state.value.data),
        }),
      )
    },
    'attachPairAction',
  )

  const resolvePairLaterAction = action(
    async (
      ctx,
      {
        pairAddress,
        network,
      }: { pairAddress: PairAddress; network: Network.Network },
    ) => {
      const key = `${network}|${pairAddress}`
      if (pending.has(key)) {
        return
      }
      pending.add(key)
      const result = await ctx.schedule(() =>
        fetchPairRequestAction(ctx, { address: pairAddress, network }),
      )
      pending.delete(key)
      if (Result.isFailure(result)) {
        return
      }
      attachPairAction(
        ctx,
        pairAddress,
        pairRegistry.resolveFromDto(ctx, result.success),
      )
    },
    'resolvePairLaterAction',
  )

  const flushBufferAction = action((ctx) => {
    const state = ctx.get(tableModel)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    const currentBuffer = ctx.get(unseenAlertsBufferAtom)
    if (currentBuffer.length === 0) {
      return
    }

    tableModel.setState(
      ctx,
      AsyncStateN.success({
        data: processUpdates(state.value.data, currentBuffer),
        hasNext: false,
      }),
    )
    unseenAlertsBufferAtom(ctx, [])
  }, 'flushBufferAction')

  onConnect(tableModel, (ctx) => {
    handleConnectedAction(ctx)

    /**
     * And again once it has actually loaded.
     *
     * Connecting is not the same as having rows: the panel opens, the read
     * starts, and the flush above finds the table still waiting and does
     * nothing. Whatever was held while it was closed would then sit there
     * until the next time it was opened.
     */
    const stopWatchingLoad = ctx.subscribe(tableModel, () => {
      handleConnectedAction(ctx)
    })

    const pausedSub = pausedReaction(ctx)
    const shownSub = shownReaction(ctx)
    return () => {
      stopWatchingLoad()
      pausedAtom(ctx, false)
      pausedSub.unsubscribe()
      shownSub.unsubscribe()
    }
  })

  const initializeSubscriptionsAction = action(() => {
    const unsubscribeDeleted = walletTrackingDeletedEvent.onCall((ctx) => {
      tableModel.load(ctx)
    })
    const unsubscribeAdded = walletTrackingAddedEvent.onCall((ctx) => {
      tableModel.load(ctx)
    })

    const unsubscribeUpdated = walletTrackingUpdatedEvent.onCall((ctx) => {
      tableModel.load(ctx)
    })

    const unsubscribeTrackingWalletAlert = trackingWalletAlertEvent.onCall(
      (ctx, shortTx) => {
        const {
          assets: { tokenLogosHost, avatarsHost },
        } = ctx.get(environmentAtom)
        const network = ctx.get(selectedNetworkAtom)
        const pairAddress = PairAddress(shortTx.pairAddress)
        /* A trade off the socket carries no card — the producer publishes
           what the chain said, and building one per trade would put the whole
           card fan-out on the ingest path. The registry usually already has
           the pair from the list or a feed; when it does not, it is fetched
           once and hung on the row afterwards. Until then the row draws, and
           only its buy button is missing. */
        const pair =
          (shortTx.pairInfo
            ? pairRegistry.resolveFromDto(ctx, shortTx.pairInfo)
            : undefined) ?? pairRegistry.get(pairAddress, network)
        const trade = TrackedWalletSwapAlert.fromDto(
          shortTx,
          tokenLogosHost,
          avatarsHost,
          pair,
        )
        if (!pair) {
          resolvePairLaterAction(ctx, { pairAddress, network })
        }
        const state = ctx.get(tableModel)
        const paused = ctx.get(pausedAtom)
        const isTableVisible = ctx.get(alertsPanelShownAtom)

        /**
         * Anything the reader is not looking at is held and counted.
         *
         * The table's own state used to be checked first, and a trade that
         * arrived while it was anything but loaded was dropped on the floor
         * — not shown, not held, not counted. That is the ordinary case: the
         * panel lives in the footer and is closed, so the table has never
         * loaded, and the badge beside it could never leave zero however many
         * trades went past. The buffer does not need the table; it is its own
         * list, and the badge counts it.
         */
        if (!isTableVisible || paused || !AsyncStateN.isSuccess(state)) {
          const currentBuffer = ctx.get(unseenAlertsBufferAtom)
          unseenAlertsBufferAtom(ctx, takeMaxItems([...currentBuffer, trade]))
          return
        }

        const updatedItems = processUpdates(state.value.data, [trade])
        const updatedState = AsyncStateN.success({
          ...state.value,
          data: updatedItems,
        })
        tableModel.setState(ctx, updatedState)

        const _dispose = () => {
          unsubscribeDeleted()
          unsubscribeAdded()
          unsubscribeUpdated()
          unsubscribeTrackingWalletAlert()
        }
      },
    )
  }, 'initializeSubscriptionsAction')

  return tableModel.pipe(
    withAssign(() => ({
      initializeSubscriptions: initializeSubscriptionsAction,
      paused: pausedAtom,
      flushBuffer: flushBufferAction,
    })),
  )
}

const processUpdates = (
  currentAlerts: Array<TrackedWalletSwapAlert.Alert>,
  newAlerts: Array<TrackedWalletSwapAlert.Alert>,
) => {
  return pipe(
    [...newAlerts, ...currentAlerts],
    Iterable.uniqBy((it) => it.id),
    Iterable.toArray,
    Array.sort(cmpByTimestamp),
    takeMaxItems,
  )
}
const cmpByTimestamp: Order.Order<TrackedWalletSwapAlert.Alert> = (a, b) => {
  return Trade.cmpByTimestampDesc(a, b)
}

// eslint-disable-next-line @reatom/unit-naming-rule
export const trackingWalletAlertsTableModel = reatomTrackingWalletAlertsTable()

export const unseenAlertsBufferAtom = atom<Array<TrackedWalletSwapAlert.Alert>>(
  [],
  'unseenAlertsBufferAtom',
)
export const unseenAlertsCountAtom = atom(
  (ctx) => ctx.spy(unseenAlertsBufferAtom).length,
  'unseenAlertsCountAtom',
)

export const initializeTrackingWalletsTableAction = action((ctx) => {
  trackingWalletAlertsTableModel.initializeSubscriptions(ctx)
}, 'initializeTrackingWalletsTableAction')
